import type { ReactNode } from 'react';

/**
 * The Souqli dashboard page header.
 *
 * One component rather than the same markup copied onto every page - the
 * treatment (warm dark ground, mashrabiya texture, two gold blooms) started on
 * the Souqli Member card, and repeating it by hand is how four pages end up
 * four slightly different shades of brown.
 *
 * `aside` is for the right-hand figure some pages carry (£0 on the free shelf,
 * a balance, a count). It collapses on narrow screens rather than squeezing
 * the title.
 */
export function PageHeader({
  eyebrow,
  title,
  accent,
  subtitle,
  aside,
  asideNote,
  children,
  gradient = false,
}: {
  eyebrow: string;
  title: ReactNode;
  /** Rendered in gold immediately after the title. */
  accent?: string;
  subtitle?: ReactNode;
  aside?: ReactNode;
  asideNote?: string;
  /** Badges, buttons or anything else that sits under the subtitle. */
  children?: ReactNode;
  /** Slightly richer ground - used where the page is the "premium" one. */
  gradient?: boolean;
}) {
  return (
    <div
      className="section-dark relative overflow-hidden rounded-bold text-cream shadow-frame -mx-2 sm:mx-0"
      style={gradient
        ? { background: 'linear-gradient(135deg,#1a1208 0%,#221A12 45%,#3a2a12 100%)' }
        : { background: '#221A12' }}
    >
      <div className="absolute inset-0 mashrabiya opacity-[0.07] pointer-events-none" />
      <span className="absolute -right-16 -top-20 w-72 h-72 rounded-full bg-gold/20 blur-3xl pointer-events-none" />
      <span className="absolute -left-10 -bottom-24 w-64 h-64 rounded-full bg-gold/10 blur-3xl pointer-events-none" />

      <div className="relative px-5 py-7 sm:px-7 sm:py-9 grid md:grid-cols-12 gap-5 sm:gap-6 items-center">
        <div className={aside ? 'md:col-span-8' : 'md:col-span-12'}>
          <div className="stamp text-gold text-[10px]">{eyebrow}</div>
          <h1 className="display text-[26px] sm:text-3xl md:text-4xl mt-2 leading-[1.05]">
            {title}{accent && <> <span className="text-gold">{accent}</span></>}
          </h1>
          {subtitle && (
            <p className="mt-3 text-cream/75 text-[13px] max-w-[62ch] leading-relaxed">{subtitle}</p>
          )}
          {children && <div className="mt-4">{children}</div>}
        </div>

        {aside && (
          <div className="md:col-span-4 text-left md:text-right">
            {aside}
            {asideNote && <div className="stamp text-cream/55 mt-2 text-[10px]">{asideNote}</div>}
          </div>
        )}
      </div>

      <div className="gold-rule" />
    </div>
  );
}
